import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ArrowUpRight, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatsBar } from "@/components/StatsBar";
import { HypeRow } from "@/components/HypeRow";
import { FeaturedShelf } from "@/components/FeaturedShelf";
import { LatestActivityFeed } from "@/components/LatestActivityFeed";
import { RaiseBidDialog } from "@/components/RaiseBidDialog";
import { fetchConfig, fetchHype, fetchHypeActivity, fetchLeaderboard } from "@/lib/api";

/** The original likes-first crowd board, unchanged — now the "Apps" tab of Home. */
export default function AppBoard() {
    const navigate = useNavigate();
    const [raiseTarget, setRaiseTarget] = useState(null);

    const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
    const hype = useQuery({ queryKey: ["hype"], queryFn: fetchHype, refetchInterval: 20000 });
    const activity = useQuery({
        queryKey: ["hype-activity"],
        queryFn: fetchHypeActivity,
        refetchInterval: 15000,
    });
    const paidOn = !!config.data?.paid_slots_enabled;
    const featured = useQuery({
        queryKey: ["leaderboard"],
        queryFn: fetchLeaderboard,
        enabled: paidOn,
        refetchInterval: 20000,
    });

    const listings = hype.data?.listings || [];
    const featuredListings = featured.data?.listings || [];
    const topBid = featured.data?.top_bid || 0;

    const goClaim = (amount) => navigate(`/outbid?bid=${amount}`);
    const onClaimRow = (row) => goClaim(row.claim_amount);

    return (
        <div>
            <section className="relative hero-wash border-b border-border overflow-hidden">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-1 px-3 py-1 text-xs text-muted-foreground mb-6">
                        <span className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse-dot" />
                        Live crowd board for fitness apps — likes rank you
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
                        List free. Rank is likes. <span className="text-brand">Earlier list = easier #1.</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                        Tweet your fitness app. Every like on that tweet pushes you up the board.
                        No account. No payment. The first listings have the shortest path to Crowd #1.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Button
                            data-testid="hero-list-button"
                            size="lg"
                            onClick={() => navigate("/list")}
                            className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold text-base"
                        >
                            <Zap className="h-5 w-5 mr-2" /> List your app
                            <ArrowUpRight className="h-4 w-4 ml-1" />
                        </Button>
                        {paidOn ? (
                            <Button
                                data-testid="hero-featured-button"
                                size="lg"
                                variant="outline"
                                onClick={() => navigate("/outbid")}
                                className="font-semibold text-base"
                            >
                                Claim a featured slot
                            </Button>
                        ) : null}
                    </div>
                    <p className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5 text-brand" /> No account · One tweet · Likes update every few minutes
                    </p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 relative z-10">
                <StatsBar stats={hype.data} variant="hype" />
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                    {paidOn ? (
                        <FeaturedShelf
                            listings={featuredListings}
                            onClaim={onClaimRow}
                            onRaise={setRaiseTarget}
                        />
                    ) : null}

                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                            Crowd board
                            <span className="inline-flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                                <span className="h-2 w-2 rounded-full bg-brand animate-pulse-dot" /> live likes
                            </span>
                        </h2>
                        <span className="text-sm text-muted-foreground tabular">{hype.data?.count || 0} listings</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                        Rank is likes on the listing tweet. Equal likes: the earlier tweet stays higher. Likes refresh every few minutes.
                    </p>

                    {hype.isLoading ? (
                        <div className="space-y-3">
                            <Skeleton className="h-32 w-full rounded-2xl" />
                            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
                        </div>
                    ) : listings.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="hype-empty">
                            <Heart className="h-10 w-10 mx-auto text-brand mb-3" />
                            <h3 className="font-heading text-lg font-semibold">No fitness apps yet</h3>
                            <p className="text-muted-foreground text-sm mt-1 mb-5">Be first. Tweet to list. Crowd #1 is easier now than tomorrow.</p>
                            <Button data-testid="empty-list-button" onClick={() => navigate("/list")} className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                                List your app
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <HypeRow row={listings[0]} featured />
                            <AnimatePresence initial={false}>
                                {listings.slice(1).map((row) => (
                                    <motion.div
                                        key={row.id}
                                        layout
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                    >
                                        <HypeRow row={row} />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-4 space-y-6">
                    <LatestActivityFeed
                        variant="hype"
                        items={activity.data?.activity}
                        loading={activity.isLoading}
                        updated={!activity.isFetching}
                    />
                </aside>
            </section>

            {paidOn ? (
                <RaiseBidDialog
                    open={!!raiseTarget}
                    onOpenChange={(v) => !v && setRaiseTarget(null)}
                    listing={raiseTarget}
                    topBid={topBid}
                />
            ) : null}
        </div>
    );
}

